"use client"

import { LinkOutlined, LoadingOutlined, MailOutlined, PhoneOutlined, UserOutlined } from "@ant-design/icons"
import CaretDownOutlined from "@ant-design/icons/lib/icons/CaretDownOutlined"
import { Form, Input, Modal, Select, Spin } from "antd"
import TextArea from "antd/es/input/TextArea"
import { useEffect, useState } from "react"
import { useMessages, useNotificationMessages } from "../../../../../../utils/notificationMessages"
import { RecruitmentData } from "../../../../../page"
import { createCandidateRequest } from "../../../../../recruitment/services/recruitment"
import useIsMobile from "../../../lib/use-is-mobile"

export const PopupRegister = ({
  isOpen,
  data,
  position,
}: {
  isOpen: boolean | undefined
  data: RecruitmentData[]
  position: number
}) => {
  const [form] = Form.useForm()
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const isMobile = useIsMobile()

  const { successNotification, errorNotification, contextHolderNotification } = useNotificationMessages()
  const { successMessages, errorMessages, contextHolderMessage } = useMessages()

  const pushNotificationSuccess = isMobile ? successMessages : successNotification
  const pushNotificationError = isMobile ? errorMessages : errorNotification
  const contextHolder = isMobile ? contextHolderMessage : contextHolderNotification

  const convertDataToSelectOptions = data.map((item: RecruitmentData) => ({
    value: item.id,
    label: `${item.attributes?.Rank} - ${item.attributes?.Title}`,
  }))

  const handleClosePopUp = () => {
    setIsFormOpen(false)
    form.resetFields()
  }

  const validateForm = async () => {
    try {
      const data = await form.validateFields()
      return data
    } catch (error) {
      console.error("Error:", error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const validatedData = await validateForm()

    if (validatedData) {
      try {
        setIsLoading(true)
        await createCandidateRequest(validatedData)

        const notificationMessage = `${
          isMobile ? `Đăng ký thành công.` : "Đăng ký thành công, SKYLINK sẽ liên hệ với bạn sớm nhất có thể."
        }`

        pushNotificationSuccess(notificationMessage)
      } catch (error) {
        console.error("Error:", error)

        pushNotificationError("Đã có lỗi xảy ra, vui lòng thử lại sau.")
      } finally {
        handleClosePopUp()
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    if (isOpen !== undefined) {
      setIsFormOpen(true)
      form.setFieldValue("position", position)
    }
  }, [isOpen])

  return (
    <>
      {contextHolder}

      <Modal
        open={isFormOpen}
        footer={null}
        classNames={{ content: "!p-0", mask: "!bg-black !bg-opacity-50 !transform !transition-all !duration-300" }}
        centered
        onCancel={handleClosePopUp}
        onClose={handleClosePopUp}
      >
        <div className={`w-full text-center bg-[#F5F6FE] rounded-lg`}>
          <div className="flex items-center justify-center">
            <div className="flex flex-col items-center py-12">
              <h2
                style={{
                  background: "-webkit-linear-gradient(62.29deg, #5bb97a 13.03%, #298cca 89.38%)",
                  backgroundClip: "text",
                  color: "transparent",
                }}
                className="font-semibold text-[32px] leading-12 mb-2 sm:mb-5 uppercase"
              >
                Ứng Tuyển vị trí
              </h2>

              <Form className="sm:w-[450px] w-[343px] transition-all" form={form}>
                <div className="space-y-4">
                  <Form.Item
                    name="fullName"
                    className="mb-0 placeholder:text-base placeholder:text-[#1A1A1AB2]"
                    rules={[{ required: true, message: "Vui lòng nhập họ và tên phụ huynh" }]}
                  >
                    <Input
                      type="text"
                      name="fullName"
                      placeholder="Nhập họ và tên"
                      className="w-full h-10 p-3 rounded-md outline-none placeholder:text-base placeholder:text-[#1A1A1AB2]"
                      required
                      prefix={<UserOutlined />}
                    />
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    rules={[
                      {
                        required: true,
                        message: "Vui lòng nhập số điện thoại",
                      },
                      {
                        pattern: /^(\+84|0)\d{9,10}$/,
                        message: "Số điện thoại không hợp lệ (ví dụ: +84912345678 hoặc 0912345678)",
                      },
                    ]}
                  >
                    <Input
                      name="phone"
                      placeholder="Số điện thoại"
                      className="w-full h-10 p-3 rounded-md outline-none placeholder:text-base placeholder:text-[#1A1A1AB2]"
                      required
                      prefix={<PhoneOutlined className="rotate-90" />}
                    />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    rules={[
                      {
                        type: "email",
                        message: "Email không hợp lệ",
                      },
                    ]}
                  >
                    <Input
                      type="email"
                      name="email"
                      placeholder="Nhập email"
                      className="w-full h-10 p-3 rounded-md outline-none placeholder:text-base placeholder:text-[#1A1A1AB2]"
                      prefix={<MailOutlined />}
                    />
                  </Form.Item>

                  <Form.Item name="position">
                    <Select
                      placeholder={
                        <span style={{ color: "#1A1A1AB2", fontSize: "16px", lineHeight: "24px" }}>
                          Chọn vị trí ứng tuyển
                        </span>
                      }
                      className="w-full rounded-md outline-none flex items-center placeholder:text-base placeholder:text-[#1A1A1AB2] h-10"
                      suffixIcon={<CaretDownOutlined />}
                      onChange={(value, option) => form.setFieldsValue({ position: value })}
                      options={convertDataToSelectOptions}
                    />
                  </Form.Item>

                  <Form.Item name="link" rules={[]}>
                    <Input
                      type="link"
                      name="link"
                      placeholder="Link CV"
                      className="w-full h-10 p-3 rounded-md outline-none placeholder:text-base placeholder:text-[#1A1A1AB2]"
                      prefix={<LinkOutlined />}
                    />
                  </Form.Item>

                  <Form.Item name="description">
                    <TextArea
                      name="description"
                      placeholder="Giới thiệu về bản thân (Không bắt buộc)"
                      rows={4}
                      className="w-full p-3 rounded-md outline-none placeholder:text-base placeholder:text-[#1A1A1AB2] "
                      required
                    />
                  </Form.Item>
                </div>

                <Form.Item label={null}>
                  <button
                    type="submit"
                    style={{
                      background: "-webkit-linear-gradient(62.29deg, #5bb97a 13.03%, #298cca 89.38%)",
                    }}
                    onClick={handleSubmit}
                    className="w-full mt-4 font-semibold text-base text-white py-3 rounded-md uppercase"
                  >
                    Gửi
                  </button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50">
            <Spin
              indicator={
                <LoadingOutlined
                  style={{
                    fontSize: 60,
                  }}
                  spin
                />
              }
            />
          </div>
        )}
      </Modal>
    </>
  )
}
